import { Button, cn, Dialog, DialogContent } from '@menage/ui';
import { Mail, Menu, Phone, Sparkles } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router';
import { setLanguage } from '@/i18n';
import { useAuth } from '@/lib/auth/AuthProvider';
import { usePublicSettings } from '@/lib/format';

export const SITE_NAV = [
  { to: '/services', key: 'services' },
  { to: '/#comment-ca-marche', key: 'how' },
  { to: '/soumission', key: 'quote' },
  { to: '/secteurs', key: 'sectors' },
  { to: '/faq', key: 'faq' },
] as const;

export function Logo({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn('flex items-center gap-2 font-semibold tracking-tight', className)}>
      <span className="flex size-8 items-center justify-center rounded-full bg-secondary text-primary" aria-hidden>
        <Sparkles className="size-[18px]" />
      </span>
      <span>{t('appName')}</span>
    </span>
  );
}

/** Scrolls to `#hash` targets (react-router does not) and to the top on page change. */
export function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      // The target may render after a lazy chunk: retry briefly.
      let tries = 0;
      const tick = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ block: 'start' });
        else if (tries++ < 20) window.setTimeout(tick, 50);
      };
      tick();
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function LanguageButton({ className }: { className?: string }) {
  const { t, i18n } = useTranslation('site');
  const next = i18n.language === 'fr' ? 'en' : 'fr';
  return (
    <Button variant="ghost" size="sm" className={className} onClick={() => setLanguage(next)} aria-label={t('nav.language')} lang={next}>
      {next.toUpperCase()}
    </Button>
  );
}

export function PublicHeader() {
  const { t } = useTranslation('site');
  const { actor } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  const accountCta = actor ? (
    <Button asChild size="sm">
      <Link to="/app">{t('nav.myAccount')}</Link>
    </Button>
  ) : (
    <Button asChild size="sm">
      <Link to="/soumission">{t('nav.cta')}</Link>
    </Button>
  );

  return (
    <header className="sticky top-0 z-30 border-b border-border/70 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85">
      <div className="container flex h-16 items-center gap-2">
        <Link to="/" aria-label={t('nav.home')} className="mr-2 shrink-0 rounded-lg">
          <Logo />
        </Link>
        <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex" aria-label={t('nav.menu')}>
          {SITE_NAV.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground',
                  isActive && !item.to.includes('#') && 'text-foreground',
                )
              }
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto hidden items-center gap-1 lg:flex">
          <LanguageButton />
          {actor ? null : (
            <Button asChild variant="ghost" size="sm">
              <Link to="/connexion">{t('nav.login')}</Link>
            </Button>
          )}
          {accountCta}
        </div>
        {/* Phones and tablets: the quote button stays visible next to the menu. */}
        <div className="ml-auto flex items-center gap-1 lg:hidden">
          {actor ? (
            <Button asChild size="sm">
              <Link to="/app">{t('nav.myAccount')}</Link>
            </Button>
          ) : (
            <Button asChild size="sm" className="px-3">
              <Link to="/soumission">{t('nav.quote')}</Link>
            </Button>
          )}
          <Button variant="ghost" size="icon" aria-label={t('nav.menu')} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
            <Menu />
          </Button>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title={t('nav.menu')} closeLabel={t('common:actions.close')}>
          <nav className="flex flex-col" aria-label={t('nav.menu')}>
            {SITE_NAV.map((item) => (
              <Link key={item.key} to={item.to} onClick={() => setOpen(false)} className="flex min-h-12 items-center rounded-lg px-3 text-base font-medium hover:bg-muted">
                {t(`nav.${item.key}`)}
              </Link>
            ))}
          </nav>
          <div className="mt-4 grid gap-2 border-t border-border pt-4">
            {actor ? (
              <Button asChild>
                <Link to="/app">{t('nav.myAccount')}</Link>
              </Button>
            ) : (
              <>
                <Button asChild>
                  <Link to="/soumission">{t('nav.cta')}</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/connexion">{t('nav.login')}</Link>
                </Button>
              </>
            )}
            <LanguageButton className="justify-start" />
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-semibold">{title}</h2>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <li>
      <Link to={to} className="rounded hover:text-foreground">
        {children}
      </Link>
    </li>
  );
}

export function PublicFooter() {
  const { t } = useTranslation('site');
  const { data: settings } = usePublicSettings();
  const company = settings?.company;
  return (
    <footer className="border-t border-border bg-surface-muted/60">
      <div className="container grid grid-cols-2 gap-x-6 gap-y-10 py-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
        <div className="col-span-2 lg:col-span-1">
          <Logo />
          <p className="mt-3 max-w-xs text-sm text-muted-foreground">{t('footer.tagline')}</p>
          {company?.phone || company?.email ? (
            <ul className="mt-4 space-y-2 text-sm">
              {company.phone ? (
                <li>
                  <a href={`tel:${company.phone.replace(/[^\d+]/g, '')}`} className="inline-flex items-center gap-2 hover:text-primary">
                    <Phone className="size-4 text-primary" aria-hidden /> {company.phone}
                  </a>
                </li>
              ) : null}
              {company.email ? (
                <li>
                  <a href={`mailto:${company.email}`} className="inline-flex items-center gap-2 hover:text-primary">
                    <Mail className="size-4 text-primary" aria-hidden /> {company.email}
                  </a>
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
        <FooterColumn title={t('footer.services')}>
          <FooterLink to="/services#residential">{t('services.residential.title')}</FooterLink>
          <FooterLink to="/services#recurring">{t('services.recurring.title')}</FooterLink>
          <FooterLink to="/services#deep">{t('services.deep.title')}</FooterLink>
          <FooterLink to="/services#commercial">{t('services.commercial.title')}</FooterLink>
        </FooterColumn>
        <FooterColumn title={t('footer.company')}>
          <FooterLink to="/a-propos">{t('nav.about')}</FooterLink>
          <FooterLink to="/secteurs">{t('nav.sectors')}</FooterLink>
          <FooterLink to="/soumission">{t('nav.quote')}</FooterLink>
        </FooterColumn>
        <FooterColumn title={t('footer.help')}>
          <FooterLink to="/faq">{t('nav.faq')}</FooterLink>
          <FooterLink to="/connexion">{t('nav.login')}</FooterLink>
          <FooterLink to="/soumission">{t('nav.cta')}</FooterLink>
        </FooterColumn>
        <FooterColumn title={t('footer.legal')}>
          <FooterLink to="/confidentialite">{t('footer.privacy')}</FooterLink>
          <FooterLink to="/conditions">{t('footer.terms')}</FooterLink>
          <FooterLink to="/annulation">{t('footer.cancellation')}</FooterLink>
        </FooterColumn>
      </div>
      <div className="border-t border-border">
        <div className="container flex flex-col gap-1 py-5 text-xs text-muted-foreground sm:flex-row sm:justify-between">
          <p>
            © {new Date().getFullYear()} {company?.name ?? t('common:appName')}. {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ----------------------------------------------------------------- building blocks */

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-sm font-semibold text-primary', className)}>{children}</p>;
}

export function SectionHeading({ eyebrow, title, text, center, as: Tag = 'h2', id }: { eyebrow?: string; title: string; text?: string; center?: boolean; as?: 'h1' | 'h2'; id?: string }) {
  return (
    <div className={cn('max-w-2xl', center && 'mx-auto text-center')}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <Tag id={id} className={cn('mt-2 font-semibold tracking-tight', Tag === 'h1' ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl')}>
        {title}
      </Tag>
      {text ? <p className={cn('mt-3 max-w-prose text-muted-foreground sm:text-lg', center && 'mx-auto')}>{text}</p> : null}
    </div>
  );
}

export function IconCircle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('flex size-11 shrink-0 items-center justify-center rounded-full bg-secondary text-primary [&_svg]:size-5', className)} aria-hidden>
      {children}
    </span>
  );
}
